import { h } from './dom';

export const MAX_SECTIONS = 4;
export const MAX_ALTERNATIVES = 4;
export const MAX_COMBINATIONS = 10;

export interface SectionInput {
  name: string;
  /** alternatives to the original, one per line in the form */
  options: string[];
}

export interface Parsed {
  sections: SectionInput[];
  combinations: number;
  /** what is wrong, in words, or null when it can be sent */
  problem: string | null;
}

/** Turn what was typed into sections; blank lines and rows are ignored, nothing is guessed. */
export function parseSections(rows: Array<{ name: string; options: string }>): Parsed {
  const sections = rows
    .map((r) => ({
      name: r.name.trim(),
      options: r.options
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    }))
    .filter((s) => s.name || s.options.length);
  const combinations = sections.reduce((n, s) => n * (1 + s.options.length), 1);
  let problem: string | null = null;
  if (sections.length < 2)
    problem =
      'A multivariate test needs at least two sections. For one section, use a normal test.';
  else if (sections.some((s) => !s.name)) problem = 'Give every section a name.';
  else if (sections.some((s) => s.options.length < 1))
    problem = 'Every section needs at least one option besides the original.';
  else if (sections.some((s) => s.options.length > MAX_ALTERNATIVES))
    problem = `A section can have at most ${MAX_ALTERNATIVES} options besides the original.`;
  else if (combinations > MAX_COMBINATIONS)
    problem = `These make ${combinations} combinations; at most ${MAX_COMBINATIONS} are allowed. Use fewer sections or fewer options.`;
  return { sections, combinations, problem };
}

/** "Headline: Shorter" style label for the number of combinations a choice makes. */
export const combinationsText = (n: number) =>
  `${n} combination${n === 1 ? '' : 's'} (each gets an equal share of visitors, so it takes longer to reach a clear result than a normal test)`;

/**
 * The section builder: rows of (name, options one per line), a live count of combinations, and a
 * read() that returns what to send (or the reason it cannot be sent yet).
 */
export function sectionsBuilder(): {
  el: HTMLElement;
  read: () => Parsed;
  reset: () => void;
} {
  const wrap = h('div', { class: 'mvt' });
  const rowsBox = h('div', {});
  const summary = h('p', { class: 'muted', 'aria-live': 'polite' });
  const rows: Array<{ name: HTMLInputElement; options: HTMLTextAreaElement; el: HTMLElement }> = [];

  const read = () =>
    parseSections(rows.map((r) => ({ name: r.name.value, options: r.options.value })));
  const update = () => {
    const p = read();
    summary.textContent = p.problem ? p.problem : `${combinationsText(p.combinations)}.`;
    summary.className = p.problem && p.sections.length ? 'err' : 'muted';
    add.disabled = rows.length >= MAX_SECTIONS;
  };

  const addRow = () => {
    const name = h('input', {
      type: 'text',
      maxlength: '60',
      autocomplete: 'off',
      placeholder: rows.length ? 'Button' : 'Headline',
    }) as HTMLInputElement;
    const options = h('textarea', {
      rows: '3',
      placeholder: 'One option per line, besides the original',
      'aria-label': 'Options',
    }) as HTMLTextAreaElement;
    const remove = h('button', { type: 'button', class: 'danger' }, 'Remove section');
    const el = h(
      'fieldset',
      { class: 'section-row' },
      h('legend', {}, `Section ${rows.length + 1}`),
      h('label', { class: 'field' }, h('span', { class: 'lbl' }, 'Section name'), name),
      h(
        'label',
        { class: 'field' },
        h('span', { class: 'lbl' }, 'Options to try (the original is always included)'),
        options,
      ),
      remove,
    );
    const row = { name, options, el };
    rows.push(row);
    remove.addEventListener('click', () => {
      if (rows.length <= 2) return;
      rows.splice(rows.indexOf(row), 1);
      el.remove();
      rows.forEach((r, i) => (r.el.querySelector('legend')!.textContent = `Section ${i + 1}`));
      update();
    });
    name.addEventListener('input', update);
    options.addEventListener('input', update);
    rowsBox.append(el);
    update();
  };
  const add = h('button', { type: 'button' }, 'Add a section') as HTMLButtonElement;
  add.addEventListener('click', () => addRow());
  const reset = () => {
    rows.splice(0).forEach((r) => r.el.remove());
    addRow();
    addRow();
  };

  wrap.append(
    h(
      'p',
      { class: 'muted' },
      'Pick parts of the page (a headline, a button) and give each some options. Every combination is shown to visitors, and you learn which option of each part works best.',
    ),
    rowsBox,
    h('p', {}, add),
    summary,
  );
  reset();
  return { el: wrap, read, reset };
}
